import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';

// Storefront Components & Pages
import Header from './components/layout/Header';
import Footer from './components/layout/Footer';
import ScrollToTop from './components/common/ScrollToTop';
import TesterAccessGate from './components/common/TesterAccessGate';
import CartDrawer from './components/cart/CartDrawer';
import Home from './pages/Home';
import Shop from './pages/Shop';
import ProductDetail from './pages/ProductDetail';
import Cart from './pages/Cart';
import Checkout from './pages/Checkout';
import Payment from './pages/Payment';
import OrderSuccess from './pages/OrderSuccess';
import PaymentFailure from './pages/PaymentFailure';
import UserLogin from './pages/UserLogin';
import UserForgotPassword from './pages/UserForgotPassword';
import UserProfile from './pages/UserProfile';
import UserOrders from './pages/UserOrders';
import UserOrderDetail from './pages/UserOrderDetail';
import UserWishlist from './pages/UserWishlist';
import Contact from './pages/Contact';
import Science from './pages/Science';
import FAQ from './pages/FAQ';
import AboutUs from './pages/AboutUs';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfUse from './pages/TermsOfUse';
import ReturnsAndRefunds from './pages/ReturnsAndRefunds';
import ShippingAndTracking from './pages/ShippingAndTracking';
import WarrantyInformation from './pages/WarrantyInformation';
import UnderMaintenance from './pages/UnderMaintenance';

// API Helpers
import {
  fetchCartFromBackend,
  addToCartBackend,
  updateCartQuantityBackend,
  removeFromCartBackend,
  clearCartBackend
} from './services/api';

// Admin Security Guard & Admin Components
import AdminProtectedRoute from './admin/components/auth/AdminProtectedRoute';
import Login from './admin/pages/Login';
import ForgotPassword from './admin/pages/ForgotPassword';
import VerifyOTP from './admin/pages/VerifyOTP';
import ResetPassword from './admin/pages/ResetPassword';
import AdminLayout from './admin/components/layout/AdminLayout';
import Dashboard from './admin/pages/Dashboard';
import Panels from './admin/pages/Panels';
import Inventory from './admin/pages/Inventory';
import Settings from './admin/pages/Settings';
import Promotions from './admin/pages/Promotions';
import ContactMessages from './admin/pages/ContactMessages';
import AdminOrders from './admin/pages/Orders';
import AdminOrderDetail from './admin/pages/OrderDetail';
import AdminCustomers from './admin/pages/Customers';
import AdminReviews from './admin/pages/Reviews';
import AdminRefunds from './admin/pages/AdminRefunds';

function StorefrontRoutes({ cartItems, handleAddToCart, handleUpdateQuantity, handleRemoveItem, handleClearCart, cartOpen, setCartOpen }) {
  const navigate = useNavigate();

  const handleBuyNow = (product) => {
    handleAddToCart(product);
    navigate('/cart');
  };

  return (
    <>
      <Header cartCount={cartItems.reduce((acc, item) => acc + (item.quantity || 1), 0)} onOpenCart={() => setCartOpen(true)} />
      <Routes>
        <Route
          path="/"
          element={
            <Home
              cartItems={cartItems}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={handleUpdateQuantity}
              onBuyNow={handleBuyNow}
              onOpenCart={() => setCartOpen(true)}
            />
          }
        />
        <Route
          path="/shop"
          element={
            <Shop
              cartItems={cartItems}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={handleUpdateQuantity}
              onBuyNow={handleBuyNow}
              onOpenCart={() => setCartOpen(true)}
            />
          }
        />
        <Route
          path="/product/:id"
          element={
            <ProductDetail
              cartItems={cartItems}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={handleUpdateQuantity}
              onBuyNow={handleBuyNow}
            />
          }
        />
        <Route
          path="/cart"
          element={
            <Cart
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveItem}
            />
          }
        />
        <Route
          path="/checkout"
          element={
            <Checkout
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveItem}
              onClearCart={handleClearCart}
            />
          }
        />
        <Route
          path="/payment"
          element={
            <Payment
              cartItems={cartItems}
              onClearCart={handleClearCart}
            />
          }
        />
        <Route path="/order-success" element={<OrderSuccess />} />
        <Route path="/success" element={<OrderSuccess />} />
        <Route path="/order-failure" element={<PaymentFailure />} />
        <Route path="/failure" element={<PaymentFailure />} />
        <Route path="/login" element={<UserLogin />} />
        <Route path="/forgot-password" element={<UserForgotPassword />} />
        <Route path="/profile" element={<UserProfile />} />
        <Route path="/orders" element={<UserOrders />} />
        <Route path="/orders/:id" element={<UserOrderDetail />} />
        <Route path="/wishlist" element={<UserWishlist cartItems={cartItems} onAddToCart={handleAddToCart} onUpdateQuantity={handleUpdateQuantity} />} />
        <Route path="/contact" element={<Contact />} />
        <Route
          path="/benefits"
          element={
            <Home
              cartItems={cartItems}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={handleUpdateQuantity}
              onBuyNow={handleBuyNow}
              onOpenCart={() => setCartOpen(true)}
            />
          }
        />
        <Route path="/science" element={<Science />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/about" element={<AboutUs />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfUse />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/returns" element={<ReturnsAndRefunds />} />
        <Route path="/returns-refunds" element={<ReturnsAndRefunds />} />
        <Route path="/shipping" element={<ShippingAndTracking />} />
        <Route path="/shipping-tracking" element={<ShippingAndTracking />} />
        <Route path="/warranty" element={<WarrantyInformation />} />
        <Route path="/warranty-info" element={<WarrantyInformation />} />
        <Route
          path="/contact"
          element={
            <Home
              cartItems={cartItems}
              onAddToCart={handleAddToCart}
              onUpdateQuantity={handleUpdateQuantity}
              onBuyNow={handleBuyNow}
              onOpenCart={() => setCartOpen(true)}
            />
          }
        />
      </Routes>
      <Footer />

      {/* Shopping Cart Drawer */}
      <CartDrawer
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cartItems={cartItems}
        onRemoveItem={handleRemoveItem}
        onUpdateQuantity={handleUpdateQuantity}
      />
    </>
  );
}

function App() {
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useState([]);

  // Load Live Backend Cart on Startup
  useEffect(() => {
    let isMounted = true;
    const syncCart = async () => {
      const data = await fetchCartFromBackend();
      if (isMounted && data && Array.isArray(data.items)) {
        setCartItems(data.items);
      }
    };
    syncCart();
    return () => { isMounted = false; };
  }, []);

  const handleAddToCart = async (product) => {
    // Optimistic Local Update
    setCartItems((prev) => {
      const exists = prev.find((item) => Number(item.id) === Number(product.id));
      const stock = product.stock !== undefined ? product.stock : 99;
      if (exists) {
        return prev.map((item) =>
          Number(item.id) === Number(product.id) ? { ...item, quantity: Math.min(stock, (item.quantity || 1) + 1) } : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });

    // Sync to Backend
    if (product.id) {
      const res = await addToCartBackend(product.id, 1, product.selected_addons || []);
      if (res && res.items) {
        setCartItems(res.items);
      }
    }
  };

  const handleUpdateQuantity = async (productId, delta, stockLimit = 99) => {
    setCartItems((prev) => {
      return prev
        .map((item) => {
          if (Number(item.id) === Number(productId) || Number(item.product_id) === Number(productId)) {
            const newQty = (item.quantity || 1) + delta;
            if (newQty <= 0) return null;
            return { ...item, quantity: Math.min(stockLimit, newQty) };
          }
          return item;
        })
        .filter(Boolean);
    });

    // Backend Sync
    const existing = cartItems.find(item => Number(item.id) === Number(productId) || Number(item.product_id) === Number(productId));
    const newQty = existing ? (existing.quantity || 1) + delta : 1;
    const cartItemId = existing?.cart_item_id || existing?.id || productId;

    if (cartItemId) {
      const res = await updateCartQuantityBackend(cartItemId, newQty);
      if (res && res.items) {
        setCartItems(res.items);
      }
    }
  };

  const handleRemoveItem = async (id) => {
    setCartItems((prev) => prev.filter((item) => (item.cart_item_id || item.id) !== id && item.id !== id));
    await removeFromCartBackend(id);
  };

  const handleClearCart = async () => {
    setCartItems([]);
    await clearCartBackend();
  };

  return (
    <Router>
      <TesterAccessGate>
        <ScrollToTop />
        <div className="single-web-app">
          <Routes>
            {/* ========================================================
                MAINTENANCE MODE (Uncomment below to enable maintenance)
               ======================================================== */}
            {/* <Route path="*" element={<UnderMaintenance />} /> */}

            {/* ==========================================
                1. USER-FACING STOREFRONT ROUTES (at /)
               ========================================== */}
            <Route
              path="/*"
              element={
                <StorefrontRoutes
                  cartItems={cartItems}
                  handleAddToCart={handleAddToCart}
                  handleUpdateQuantity={handleUpdateQuantity}
                  handleRemoveItem={handleRemoveItem}
                  handleClearCart={handleClearCart}
                  cartOpen={cartOpen}
                  setCartOpen={setCartOpen}
                />
              }
            />

            {/* ==========================================
                2. PUBLIC ADMIN AUTH ROUTES (at /admin)
               ========================================== */}
            <Route path="/admin/login" element={<Login />} />
            <Route path="/admin/forgot-password" element={<ForgotPassword />} />
            <Route path="/admin/verify-otp" element={<VerifyOTP />} />
            <Route path="/admin/reset-password" element={<ResetPassword />} />

            {/* ==========================================
                3. STRICT PROTECTED ADMIN PANEL ROUTES
               ========================================== */}
            <Route element={<AdminProtectedRoute />}>
              <Route path="/admin" element={<AdminLayout />}>
                <Route index element={<Navigate to="/admin/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="panels" element={<Panels />} />
                <Route path="inventory" element={<Inventory />} />
                <Route path="messages" element={<ContactMessages />} />
                <Route path="settings" element={<Settings />} />
                <Route path="orders" element={<AdminOrders />} />
                <Route path="orders/:id" element={<AdminOrderDetail />} />
                <Route path="refunds" element={<AdminRefunds />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="promotions" element={<Promotions />} />
              </Route>
            </Route>
          </Routes>
        </div>
      </TesterAccessGate>
    </Router>
  );
}

export default App;
